import { useCallback } from 'react';
import { CliError, CliErrorCode } from '@wix/cli-error';
import {
  DeploymentStatus,
  useDocumentManagementClient,
} from '@wix/document-management-client';
import { useHttpClient } from '@wix/cli-http-client-react';
import { getTestOverrides } from '@wix/cli-test-overrides';
import { deployDocument } from '@wix/anyide-document-deployment';
import { useProjectModel } from '../../model.js';
import { createSiteFsManager } from '../../site-fs-manager.js';
import { useDeploySitePreview } from './useDeploySitePreview.js';

const { pollInterval } = getTestOverrides();

function toSiteUrl(deploymentUrl: string) {
  const url = new URL(deploymentUrl);
  url.search = '';
  return url.toString();
}

export function useCreatePreview() {
  const dmClient = useDocumentManagementClient();
  const { model } = useProjectModel();
  const httpClient = useHttpClient({ type: 'editor' });
  const deploySitePreview = useDeploySitePreview();

  return useCallback(async () => {
    const siteFsManager = await createSiteFsManager(model.projectFolder);

    const [code, document] = await Promise.all([
      siteFsManager.getCode(),
      siteFsManager.getDocument(),
    ]);

    const { branchId, revision } = await deployDocument(document, {
      httpClient,
      pollInterval,
    }).catch((error: unknown) => {
      throw new CliError({
        code: CliErrorCode.FailedToDeployDocument(),
        cause: error,
      });
    });

    const preview = await deploySitePreview({ branchId, revision, code });

    return {
      deploymentId: preview.deploymentId,
      previewUrl: preview.deploymentShortUrl ?? preview.deploymentUrl,
      siteUrl: toSiteUrl(preview.deploymentUrl),
      deploymentStatus: preview.isPublishPipelineDeployment
        ? await dmClient.getDeploymentDetails(preview.deploymentId)
        : DeploymentStatus.Success(),
    };
  }, [model.projectFolder, httpClient, deploySitePreview, dmClient]);
}
